/**
 * 模块配置系统的权限控制指令
 * 提供 v-perm 指令用于模板中的权限控制，基于模块配置系统
 */

import type { App, Directive } from 'vue'
import { useUserStore } from '@/stores/user'
import { hasModulePermission, hasRole, isSuperAdmin } from '@/utils/permission-utils'

/**
 * 权限指令类型
 */
type PermissionValue = string | string[] | {
  permission?: string | string[]
  role?: string | string[]
  mode?: 'any' | 'all'
  fallback?: 'hide' | 'disable' | 'readonly'
}

/**
 * 标准化的权限名称格式
 * 所有权限名称应遵循 module:route:action 格式
 * 例如: system:user:view, tenant:list:view, home:dashboard:view
 */
export const PERMISSION_FORMAT_REGEX = /^[a-z][a-z0-9-]+:[a-z][a-z0-9-]+:[a-z][a-z0-9-]+$/

/**
 * 处理权限检查
 */
function checkPermission(value: PermissionValue): boolean {
  if (!value) return true

  // 超级管理员拥有所有权限
  const userStore = useUserStore()
  if (userStore.isSuperAdmin || isSuperAdmin()) {
    return true
  }

  // 字符串或数组形式的权限
  if (typeof value === 'string') {
    // 验证权限格式是否符合标准
    if (PERMISSION_FORMAT_REGEX.test(value)) {
      // 解析权限字符串
      const [moduleId, , action] = value.split(':')
      // 使用新的权限工具函数检查
      return hasModulePermission(moduleId, action)
    } else {
      console.warn(`权限格式不符合标准: ${value}，应遵循 module:route:action 格式`)
      return false
    }
  }

  if (Array.isArray(value)) {
    // 对数组中的每个权限进行格式验证
    const validPermissions = value.filter(p => PERMISSION_FORMAT_REGEX.test(p))
    if (validPermissions.length !== value.length) {
      console.warn('部分权限格式不符合标准，应遵循 module:route:action 格式')
    }
    // 使用新的权限工具函数检查
    return validPermissions.some(p => {
      const [moduleId, , action] = p.split(':')
      return hasModulePermission(moduleId, action)
    })
  }

  // 对象形式的复杂权限配置
  if (typeof value === 'object') {
    const { permission, role, mode = 'any' } = value

    let hasPermission = true
    let hasRole = true

    // 检查权限
    if (permission) {
      if (typeof permission === 'string') {
        // 验证单个权限格式
        if (PERMISSION_FORMAT_REGEX.test(permission)) {
          // 使用新的权限工具函数检查
          const [moduleId, _, action] = permission.split(':')
          hasPermission = hasModulePermission(moduleId, action)
        } else {
          console.warn(`权限格式不符合标准: ${permission}，应遵循 module:route:action 格式`)
          hasPermission = false
        }
      } else if (Array.isArray(permission)) {
        // 验证数组中的权限格式
        const validPermissions = permission.filter(p => PERMISSION_FORMAT_REGEX.test(p))
        if (validPermissions.length !== permission.length) {
          console.warn('部分权限格式不符合标准，应遵循 module:route:action 格式')
        }

        // 使用新的权限工具函数检查
        const checkSinglePermission = (p: string) => {
          const [moduleId, _, action] = p.split(':')
          return hasModulePermission(moduleId, action)
        }

        hasPermission = mode === 'all'
          ? validPermissions.every(p => checkSinglePermission(p))
          : validPermissions.some(p => checkSinglePermission(p))
      }
    }

    // 检查角色
    if (role) {
      hasRole = hasRole(role)
    }

    // 根据模式返回结果
    return mode === 'all' ? (hasPermission && hasRole) : (hasPermission || hasRole)
  }

  return false
}

/**
 * 应用权限控制效果
 */
function applyPermissionEffect(
  el: HTMLElement,
  hasPermission: boolean,
  fallback: 'hide' | 'disable' | 'readonly' = 'hide'
) {
  if (hasPermission) {
    // 有权限时恢复元素状态
    el.style.display = el.dataset.originalDisplay || ''
    el.removeAttribute('disabled')
    el.removeAttribute('readonly')
    el.classList.remove('permission-disabled', 'permission-readonly', 'permission-controlled')
  } else {
    // 无权限时根据fallback策略处理
    // 添加权限控制样式类
    el.classList.add('permission-controlled')

    switch (fallback) {
      case 'hide':
        el.style.display = 'none'
        break
      case 'disable':
        el.setAttribute('disabled', 'true')
        el.classList.add('permission-disabled')
        if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
          el.disabled = true
        } else if (el.tagName.toLowerCase().startsWith('el-')) {
          // 处理Element Plus组件
          el.setAttribute('disabled', 'true')
        }
        break
      case 'readonly':
        el.setAttribute('readonly', 'true')
        el.classList.add('permission-readonly')
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          el.readOnly = true
        } else if (el.tagName.toLowerCase().startsWith('el-')) {
          // 处理Element Plus组件
          el.setAttribute('readonly', 'true')
        }
        break
    }

    // 触发自定义事件，通知组件权限变更
    el.dispatchEvent(new CustomEvent('permission-effect', {
      detail: { effect: fallback },
      bubbles: true
    }))
  }
}

/**
 * 权限指令 v-perm
 */
const permDirective: Directive<HTMLElement, PermissionValue> = {
  mounted(el, binding) {
    const { value } = binding
    const fallback = typeof value === 'object' && value.fallback ? value.fallback : 'hide'
    const hasPermission = checkPermission(value)

    // 保存原始状态
    el.dataset.originalDisplay = el.style.display || ''

    applyPermissionEffect(el, hasPermission, fallback)
  },

  updated(el, binding) {
    const { value } = binding
    const fallback = typeof value === 'object' && value.fallback ? value.fallback : 'hide'
    const hasPermission = checkPermission(value)

    applyPermissionEffect(el, hasPermission, fallback)
  },

  unmounted(el) {
    // 清理添加的类和属性
    el.classList.remove('permission-disabled', 'permission-readonly')
    delete el.dataset.originalDisplay
  }
}

/**
 * 角色指令 v-role
 */
const roleDirective: Directive<HTMLElement, string | string[]> = {
  mounted(el, binding) {
    const { value } = binding
    let roleExists = false

    // 使用新的角色检查工具函数
    roleExists = hasRole(value)

    // 保存原始状态
    el.dataset.originalDisplay = el.style.display || ''

    if (!roleExists) {
      el.style.display = 'none'
    }
  },

  updated(el, binding) {
    const { value } = binding
    let roleExists = false

    // 使用新的角色检查工具函数
    roleExists = hasRole(value)

    if (roleExists) {
      el.style.display = el.dataset.originalDisplay || ''
    } else {
      el.style.display = 'none'
    }
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * 权限指令工具函数
 */
export const permissionUtils = {
  /**
   * 检查元素是否有权限
   */
  checkElementPermission(el: HTMLElement): boolean {
    const permissionAttr = el.getAttribute('v-perm')
    const roleAttr = el.getAttribute('v-role')

    if (permissionAttr) {
      try {
        const permission = JSON.parse(permissionAttr)
        return checkPermission(permission)
      } catch {
        return checkPermission(permissionAttr)
      }
    }

    if (roleAttr) {
      try {
        const roles = JSON.parse(roleAttr)
        return Array.isArray(roles)
          ? roles.some(r => useUserStore().roles.includes(r))
          : useUserStore().roles.includes(roles)
      } catch {
        return useUserStore().roles.includes(roleAttr)
      }
    }

    return true
  },

  /**
   * 批量更新权限元素
   */
  updatePermissionElements(container: HTMLElement = document.body) {
    const permissionElements = container.querySelectorAll('[v-perm], [v-role]')

    permissionElements.forEach(el => {
      if (el instanceof HTMLElement) {
        const hasPermission = this.checkElementPermission(el)
        const fallback = el.getAttribute('data-fallback') || 'hide'
        applyPermissionEffect(el, hasPermission, fallback as any)
      }
    })
  }
}

/**
 * 注册所有权限指令
 */
export function setupModulePermissionDirectives(app: App) {
  const gp: any = app.config.globalProperties
  if (gp.__modulePermissionDirectivesInstalled) {
    return
  }
  gp.__modulePermissionDirectivesInstalled = true

  app.directive('perm', permDirective)

  if (!gp.$permission) {
    gp.$permission = {
      ...permissionUtils,
      hasModulePermission,
      hasRole,
      isSuperAdmin
    }
  }

  if (!gp.__modulePermissionProvided) {
    app.provide('permission', {
      hasModulePermission,
      hasRole,
      isSuperAdmin,
      checkPermission
    })
    gp.__modulePermissionProvided = true
  }

}

/**
 * 权限指令样式
 */
export const permissionDirectiveStyles = `
.permission-disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: none !important;
}

.permission-readonly {
  cursor: not-allowed !important;
  background-color: #f5f5f5 !important;
  pointer-events: none !important;
}

.permission-hidden {
  display: none !important;
}
`

// 自动注入样式
if (typeof window !== 'undefined') {
  const styleId = 'module-permission-directive-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = permissionDirectiveStyles
    document.head.appendChild(style)
  }
}

export {
  permDirective,
  roleDirective
}
